/* eslint-disable @typescript-eslint/naming-convention */
'use client';
import React, { KeyboardEvent, useEffect, useState } from 'react';

import ErrorModal from './ErrorModal';
import { PanConfirmScreen, PanEditComponent, PanScreenInitial } from './PanStage/PanScreenInitial';
import Modal from '@/lib/Modal';
import { useGlobalStore } from '@/store';
import { CUSTOMER_ACCESS_TOKEN_KEY, DATA_HASH_KEY, PAN_CARD_REGEX } from '@/constants';
import SecuredByRazorpay from '@/lib/SecuredByRazorpay';
import { fetchNameFromPan, updateCustomerDetails } from '@/api/customer';
import { updatePayment } from '@/api/payment';
import { formRequest } from '@/utils/helpers';
import { IUser } from '@/@types/globals';
import { getUserLoggedInStatus, trackHelper } from '@/analytics/utils';
import { ANALYTICS_ACTIONS, EVENT_NAMES } from '@/analytics/constants';

interface CollectPanModalProps {
  isOpen: boolean;
  onSuccess: (user: IUser) => void;
  onDismiss: (showError: boolean) => void;
}

interface CollectPanError {
  title: string;
  description: string;
  cta: string;
  onCtaClick: () => unknown;
  onDismiss: () => unknown;
}

type EditPanScreen = 'initial' | 'edit' | 'confirmation';

const CollectPanModal: React.FC<CollectPanModalProps> = ({ isOpen, onDismiss, onSuccess }) => {
  const [name, setName] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(isOpen);
  const [panError, setPanError] = useState('');
  const [panInput, setPanInput] = useState('');
  const userId = useGlobalStore((store) => store.user.id);
  const [isCTALoading, setIsCTALoading] = useState(false);
  const setUser = useGlobalStore((store) => store.setUser);
  const { xDataToken, userAccessToken, user, setXDataToken, activeView } = useGlobalStore();
  const [error, setError] = useState<CollectPanError | null>(null);
  const [modalScreen, setModalScreen] = useState<EditPanScreen>('initial');
  const [showPanRetryError, setShowPanRetryError] = useState(false);

  const fetchPanDetails = async (): Promise<void> => {
    // Track PAN submit click
    trackHelper({
      actionName: ANALYTICS_ACTIONS.CLICK,
      objectName: EVENT_NAMES.PAN_SUBMIT,
      screen: activeView,
      properties: {
        contact: user.contact,
        fc_customer_id: user.id,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        pan_filled: PAN_CARD_REGEX.test(panInput),
      },
    });
    if (PAN_CARD_REGEX.test(panInput)) {
      setIsCTALoading(true);
      setShowPanRetryError(false);
      try {
        const userData = await fetchNameFromPan(panInput, {
          [CUSTOMER_ACCESS_TOKEN_KEY]: userAccessToken ?? '',
        });
        setIsCTALoading(false);

        if (userData.data) {
          setName(userData.data.name ?? '');
          setUser(userData.data);
          if (userData.headers?.xHash) {
            setXDataToken(userData.headers.xHash);
          }
          setModalScreen('confirmation');
        } else if (userData.error) {
          // If PAND edit failed due to attempt exhaustion
          // show the PAN confirmation screen only with error message and an option to cancel the payment
          if (userData.error.description?.includes('maximum edits allowed for PAN')) {
            setModalScreen('confirmation');
            setShowPanRetryError(true);
            setPanError(userData.error.description || 'Error fetch PAN details');
            // If the user is on details close the popup and show the error message on the Details screen instead.
            // eslint-disable-next-line max-depth
            if (activeView === 'DETAILS') {
              onDismiss(true);
            }
          } else if (userData.error.description) {
            setPanError(userData.error.description);
          }
        }
      } catch (err) {
        setShowPanRetryError(false);
        setIsCTALoading(false);
        setPanError('Unable to verify PAN. Try again');
      }
    } else {
      setPanError('Enter a 10-digit alphanumeric PAN number only');
    }
  };

  const updatePan = async (): Promise<void> => {
    // track submit PAN click
    trackHelper({
      actionName: ANALYTICS_ACTIONS.CLICK,
      objectName: EVENT_NAMES.PAN_CONFIRM,
      screen: activeView,
      properties: {
        contact: user.contact,
        fc_customer_id: user.id,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        pan_filled: true,
      },
    });

    if (PAN_CARD_REGEX.test(panInput) && name) {
      setIsCTALoading(true);
      const updatedUserRes = await updateCustomerDetails(
        { id: userId, pan: panInput, name, dob: user.dob },
        {
          [DATA_HASH_KEY]: xDataToken ?? '',
          [CUSTOMER_ACCESS_TOKEN_KEY]: userAccessToken ?? '',
        },
      );

      if (updatedUserRes.success && updatedUserRes.data) {
        setUser(updatedUserRes.data);
        onSuccess(updatedUserRes.data);
      } else {
        setIsModalOpen(false);
        setError({
          title: 'Error updating PAN details',
          description: 'Error updating your PAN details. Please try again',
          cta: 'Try Again',
          onCtaClick: (): void => {
            setError(null);
            setIsModalOpen(true);
            setIsCTALoading(false);
          },
          onDismiss: (): void => {
            setError(null);
            setIsModalOpen(true);
            setIsCTALoading(false);
          },
        });
      }
      setIsCTALoading(false);
    }
  };

  const editPan = (): void => {
    setName('');
    setModalScreen('edit');
    // Track Edit PAN clicked
    trackHelper({
      actionName: ANALYTICS_ACTIONS.CLICK,
      objectName: EVENT_NAMES.PAN_EDIT,
      screen: activeView,
      properties: {
        contact: user.contact,
        fc_customer_id: user.id,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        pan_filled: true,
      },
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      // submit pan on enter press
      fetchPanDetails();
    }
  };

  const cancelPayment = async (): Promise<void> => {
    try {
      const res = await updatePayment({
        status: 'failed',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        failure_reason: 'cancelled_by_user',
      });
      if (res.data.gateway_payload) {
        const { url, payload, method } = res.data.gateway_payload;
        formRequest(url, payload, method);
      }
    } catch {
      //
    }
  };

  useEffect(() => {
    // Dont show the initial screen for PAN edit from details screen
    if (activeView === 'DETAILS') {
      setModalScreen('edit');
    }
    // track Pan screen render
    trackHelper({
      actionName: ANALYTICS_ACTIONS.RENDER,
      objectName: EVENT_NAMES.PAN_POPUP_RENDER,
      screen: activeView,
      properties: {
        contact: user.contact,
        fc_customer_id: user.id,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
      },
    });
  }, []);

  return (
    <>
      <Modal onDismiss={onDismiss} isOpen={isModalOpen}>
        <Modal.Body>
          <div className="d:flex d:flex-col d:w-1/2">
            {modalScreen === 'initial' ? (
              <PanScreenInitial
                handleClick={(): void => {
                  setModalScreen('edit');
                }}
              />
            ) : null}
            {modalScreen === 'edit' ? (
              <PanEditComponent
                panError={panError}
                panValue={panInput}
                handlePanChange={(e): void => {
                  setPanInput(e.target.value.toUpperCase());
                  setPanError('');
                }}
                handleKeyDown={handleKeyDown}
                isLoading={isCTALoading}
                handleClick={fetchPanDetails}
              />
            ) : null}
            {modalScreen === 'confirmation' ? (
              <PanConfirmScreen
                name={name}
                showPanRetryError={showPanRetryError}
                panError={panError}
                panInput={panInput}
                isCTALoading={isCTALoading}
                cancelPayment={cancelPayment}
                updatePan={updatePan}
                editPan={editPan}
              />
            ) : null}
            <SecuredByRazorpay />
          </div>
        </Modal.Body>
      </Modal>
      {error ? (
        <ErrorModal
          isOpen={Boolean(error)}
          onDismiss={error.onDismiss}
          title={error.title}
          description={error.description}
          ctaText={error.cta}
          onCtaClick={error.onCtaClick}
        />
      ) : null}
    </>
  );
};

export default CollectPanModal;
